/**
 * Switch User (admin impersonation) — client session plumbing.
 *
 * Two distinct landing modes, mirroring the two session stores the app
 * already uses:
 *
 *   App impersonation (internal-role target → standard dashboard)
 *     The operator's own session is stashed in sessionStorage under
 *     `waypoint:impersonator-session` (survives the reload, never leaves
 *     the browser), then localStorage is swapped to the impersonated
 *     user's token and the page is HARD-reloaded. A full reload wipes
 *     every in-memory cache (react-query resolved-permissions, the auth
 *     + permissions Zustand stores) so the app re-hydrates as the
 *     impersonated user — the session therefore carries exactly the
 *     target's privileges, never the operator's. The header shows an
 *     "Exit" control (driven by the presence of this slot) that restores
 *     the operator's session.
 *
 *   Portal impersonation (Customer-role target → Customer Portal)
 *     Parity with Phase 34: the operator's admin session in localStorage
 *     is left UNTOUCHED; the portal session is written to sessionStorage
 *     under `waypoint:customer-session` and we land on /customer-portal,
 *     whose own "Escape button" discards it and resumes admin.
 */

import { withAdminRefreshSettled } from './token-refresh';

const IMPERSONATOR_SESSION_KEY = 'waypoint:impersonator-session';
const CUSTOMER_PORTAL_SESSION_KEY = 'waypoint:customer-session';
const CUSTOMER_PORTAL_ACTIVE_TAB_KEY = 'waypoint:customer-active-tab';
// #1518 — carrier-portal impersonation lands here; CarrierPortalPage reads
// its session from this same sessionStorage key.
const CARRIER_PORTAL_SESSION_KEY = 'waypoint:carrier-session';

export interface ImpersonatedAppUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: string;
}

interface ImpersonatorSession {
  /** The operator's own session, restored verbatim on exit. */
  admin: {
    accessToken: string | null;
    user: string | null;
    refreshToken: string | null;
  };
  /** Who the operator is currently viewing as (for the header banner). */
  viewingAs: { name: string; role: string };
}

export function hasImpersonatorSession(): boolean {
  try {
    return !!sessionStorage.getItem(IMPERSONATOR_SESSION_KEY);
  } catch {
    return false;
  }
}

export function getImpersonatorSession(): ImpersonatorSession | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATOR_SESSION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonatorSession) : null;
  } catch {
    return null;
  }
}

/**
 * Discard the stashed operator session without restoring it.
 *
 * ATL-001145 (SEC-036): used by logout. `exitImpersonation` puts the
 * operator back in their own session; signing out has to do the opposite —
 * drop the stash so their accessToken/refreshToken don't survive in
 * sessionStorage after they have signed out on a shared device.
 */
export function clearImpersonatorSession(): void {
  try {
    sessionStorage.removeItem(IMPERSONATOR_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Enter an app-level impersonation: stash the operator's session, swap to
 * the impersonated user's token, then hard-reload the dashboard.
 *
 * ATL-001144: waits for any in-flight refresh of the operator's session first,
 * so the stash never captures a refresh token that is being rotated away.
 */
export function enterAppImpersonation(user: ImpersonatedAppUser, accessToken: string): Promise<void> {
  return withAdminRefreshSettled(() => stashAndSwapToImpersonatedUser(user, accessToken));
}

function stashAndSwapToImpersonatedUser(user: ImpersonatedAppUser, accessToken: string): void {
  const admin: ImpersonatorSession['admin'] = {
    accessToken: localStorage.getItem('accessToken'),
    user: localStorage.getItem('user'),
    refreshToken: localStorage.getItem('refreshToken'),
  };
  const viewingAs = {
    name: `${user.firstName} ${user.lastName}`.trim() || user.email,
    role: user.role,
  };

  try {
    sessionStorage.setItem(
      IMPERSONATOR_SESSION_KEY,
      JSON.stringify({ admin, viewingAs } satisfies ImpersonatorSession),
    );
  } catch {
    /* private mode / quota — exit would be unrecoverable, so bail loudly */
    throw new Error('Unable to start impersonation: browser storage is unavailable.');
  }

  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem(
    'user',
    JSON.stringify({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      organizationId: user.organizationId,
    }),
  );
  // Impersonation tokens are short-lived and non-refreshable. Drop the
  // operator's refresh token so a stray /auth/refresh can never mint a
  // token for the wrong identity while impersonating.
  localStorage.removeItem('refreshToken');

  window.location.assign('/dashboard');
}

/**
 * Leave an app-level impersonation, restore the operator's own session,
 * and hard-reload back to the dashboard.
 */
export function exitImpersonation(): void {
  const saved = getImpersonatorSession();
  try {
    sessionStorage.removeItem(IMPERSONATOR_SESSION_KEY);
  } catch {
    /* ignore */
  }

  const restore = (key: string, value: string | null | undefined) => {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  };
  if (saved?.admin) {
    restore('accessToken', saved.admin.accessToken);
    restore('user', saved.admin.user);
    restore('refreshToken', saved.admin.refreshToken);
  }

  window.location.assign('/dashboard');
}

/**
 * Write a customer-portal impersonation session and land in the portal.
 * Leaves the operator's localStorage admin session untouched (Phase 34).
 */
export function enterPortalImpersonation(session: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(CUSTOMER_PORTAL_SESSION_KEY, JSON.stringify(session));
    sessionStorage.removeItem(CUSTOMER_PORTAL_ACTIVE_TAB_KEY);
  } catch {
    throw new Error('Unable to start portal impersonation: browser storage is unavailable.');
  }
  window.location.assign('/customer-portal');
}

/**
 * #1518 — Write a carrier-portal impersonation session and land in the Carrier
 * Portal. Mirrors `enterPortalImpersonation` but targets the carrier session
 * key + route. Leaves the operator's localStorage admin session untouched.
 */
export function enterCarrierPortalImpersonation(session: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(CARRIER_PORTAL_SESSION_KEY, JSON.stringify(session));
  } catch {
    throw new Error('Unable to start portal impersonation: browser storage is unavailable.');
  }
  window.location.assign('/carrier-portal');
}
